'use client';

import { useState } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { InputGroup } from '@ravago/shared/radiance/components/input-field/components/InputGroup/InputGroup';
import { useTranslations } from '@ravago/shared/translations/client';

import { ProductPriceCardItem } from '../models/product-price-card-item.model';
import { ProductPriceListFiltersValue } from '../models/product-price-list-filters-value';

import { ProductPriceListFilterFields } from './product-price-list-filter-fields';
import { ProductPriceListFilterFlyout } from './product-price-list-filter-flyout';

interface Props {
   productPriceCardItems: ProductPriceCardItem[];
   value: ProductPriceListFiltersValue;
   onChange: (value: ProductPriceListFiltersValue) => void;
}

export const ProductPriceListFilters = ({ productPriceCardItems, value, onChange }: Props) => {
   const translate = useTranslations();

   const [showFilters, setShowFilters] = useState(true);
   const [flyoutOpen, setFlyoutOpen] = useState(false);

   const getFilterCount = () => {
      let count = 0;
      if (value.brand) count += 1;
      if (value.polymerType) count += 1;
      if (value.orderedFromDate) count += 1;
      if (value.orderedUntilDate) count += 1;
      return count;
   };

   const getBadge = () => {
      const count = getFilterCount();
      return count === 0 ? undefined : count.toString();
   };

   const handleClear = () => {
      onChange({ search: value.search, brand: '', polymerType: '', orderedFromDate: '', orderedUntilDate: '' });
   };

   const handleSearchChange = (newSearch: string) => {
      onChange({ ...value, search: newSearch });
   };

   const handleFilterChanged = (newValue: ProductPriceListFiltersValue) => {
      onChange(newValue);
   };

   return (
      <>
         <Box direction="column">
            <Box>
               <InputGroup
                  value={value.search}
                  onChange={handleSearchChange}
                  label={translate('prices.search-placeholder')}
                  actionIcon="search"
                  clearable
               />
               <BreakpointSwitch excludeViews={['phone', 'tab-port']}>
                  <Button
                     variant="tertiary"
                     onClick={() => setShowFilters((curr) => !curr)}
                     badge={showFilters ? undefined : getBadge()}
                     leadingIcon="sliders-h"
                  >
                     {showFilters ? translate('prices.toggle-filter-hide') : translate('prices.toggle-filter')}
                  </Button>
               </BreakpointSwitch>
               <BreakpointSwitch includeViews={['phone', 'tab-port']}>
                  <IconButton onClick={() => setFlyoutOpen(true)} badge={getBadge()} icon="sliders-h" title={translate('prices.toggle-filter')} />
               </BreakpointSwitch>
            </Box>

            <BreakpointSwitch excludeViews={['phone', 'tab-port']}>
               {showFilters && (
                  <Box>
                     <ProductPriceListFilterFields productPriceCardItems={productPriceCardItems} value={value} onChange={handleFilterChanged} />
                     {getFilterCount() > 0 && (
                        <Button variant="tertiary" onClick={handleClear}>
                           {translate('product.filter.label')}
                        </Button>
                     )}
                  </Box>
               )}
            </BreakpointSwitch>
         </Box>

         <ProductPriceListFilterFlyout
            open={flyoutOpen}
            productPriceCardItems={productPriceCardItems}
            value={value}
            onChange={handleFilterChanged}
            onClear={handleClear}
            onClose={() => setFlyoutOpen(false)}
         />
      </>
   );
};
